import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import { Icons } from "../components/Icons";
import { TableSkeleton, AdminAccreditationSkeleton } from "../components/SkeletonLoader";
import { useWebSocketEvent } from "../hooks/useWebSocket";
import {
  getAllOrganizations,
  getAllUsers,
  getAccreditationChecklist,
  getAccreditationDocuments,
  updateAccreditationStatus,
  updateOrganizationStatus,
  getAccreditationRemarks,
  addAccreditationRemark,
  BASE_URL,
} from "../services/api";

// --- Accreditation Details Page ---
const AccreditationDetails = ({ org, onBack }) => {
  const processType = (org?.orgType === "existing") ? "reaccreditation" : "accreditation";
  const [selectedDocument, setSelectedDocument] = useState("");
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [reviewConfirmation, setReviewConfirmation] = useState({
    action: null,
    document: null,
    isOpen: false,
  });
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [orgStatus, setOrgStatus] = useState(null);
  const [docReviewDropdownOpen, setDocReviewDropdownOpen] = useState(false);
  const [docReviewDecisions, setDocReviewDecisions] = useState({});
  const [isExpandedView, setIsExpandedView] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewMedium, setInterviewMedium] = useState('');
  const [pendingInterviewOrg, setPendingInterviewOrg] = useState(null);

  // Real documents fetched from the backend for this organization
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState([]);
  const [reviewNote, setReviewNote] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);

  // Maps the backend organization status to the frontend status label
  const mapOrgStatusToLabel = (status) => {
    if (status === "for_endorsement_accredited") return "For Endorsement to be Accredited";
    if (status === "for_endorsement_reaccredited") return "For Endorsement to be Re-accredited";
    if (status === "for_interview") return "For Interview";
    return null;
  };

  // Sets the initial org status from the org passed in
  useEffect(() => {
    if (org.rawStatus) {
      setOrgStatus(mapOrgStatusToLabel(org.rawStatus));
    }
  }, [org.rawStatus]);

  // Maps backend document status to the frontend review label
  const mapDocStatus = (status) => {
    if (!status) return "";
    const s = String(status.value || status).toLowerCase().replace(/[-_]/g, " ").trim();
    if (s === "reviewed" || s === "approved") return "Reviewed";
    if (s === "for revision" || s === "for_revision" || s === "revision") return "For Revision";
    if (s === "rejected" || s === "reject") return "Rejected";
    return "";
  };

  // Returns the status label and color for an attachment item or document
  const getAttachmentStatusLabel = (item) => {
    if (!item) return { text: "Not Submitted", color: "text-gray-400" };
    const docId = item.accreditation_document_id || item.reaccreditation_document_id;
    const docNameNorm = (item.document_name || "").trim().toLowerCase();

    // 1. Check in-memory/optimistic review decision by ID or docName
    if (docId && docReviewDecisions[docId]) {
      const dec = docReviewDecisions[docId];
      if (dec === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
      if (dec === "For Revision") return { text: "For Revision", color: "text-orange-600" };
      if (dec === "Rejected") return { text: "Rejected", color: "text-red-600" };
    }
    for (const [key, decision] of Object.entries(docReviewDecisions)) {
      if ((key || "").trim().toLowerCase() === docNameNorm && decision) {
        if (decision === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
        if (decision === "For Revision") return { text: "For Revision", color: "text-orange-600" };
        if (decision === "Rejected") return { text: "Rejected", color: "text-red-600" };
      }
    }

    // 2. Check the document record if it has a direct status
    if (item.status) {
      const mapped = mapDocStatus(item.status);
      if (mapped === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
      if (mapped === "For Revision") return { text: "For Revision", color: "text-orange-600" };
      if (mapped === "Rejected") return { text: "Rejected", color: "text-red-600" };
      const s = String(item.status).toLowerCase().replace(/[-_]/g, " ").trim();
      if (s === "not submitted" || s === "not_submitted") return { text: "Not Submitted", color: "text-gray-400" };
    }

    // 3. Check matching documents in `docs`
    const matchedDocs = docs.filter(
      (d) => (d.document_name || "").trim().toLowerCase() === docNameNorm
    );
    if (matchedDocs.length > 0) {
      const latestDoc = matchedDocs[matchedDocs.length - 1];
      const mapped = mapDocStatus(latestDoc.status);
      if (mapped === "Reviewed") return { text: "Reviewed", color: "text-green-600" };
      if (mapped === "For Revision") return { text: "For Revision", color: "text-orange-600" };
      if (mapped === "Rejected") return { text: "Rejected", color: "text-red-600" };
      return { text: "Uploaded", color: "text-green-600" };
    }

    return { text: "Uploaded", color: "text-green-600" };
  };

  // Fetches all accreditation documents submitted by this organization
  const fetchDocuments = async (preserveDocName, isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const res = await getAccreditationDocuments(processType, org.id);
      const normalizedDocs = res.data.map(d => ({
        ...d,
        accreditation_document_id: d.accreditation_document_id || d.reaccreditation_document_id
      }));
      setDocs(normalizedDocs);

      if (res.data.length > 0) {
        if (preserveDocName && res.data.some((d) => d.document_name === preserveDocName)) {
          setSelectedDocument(preserveDocName);
        } else {
          setSelectedDocument((prev) => (prev && res.data.some((d) => d.document_name === prev) ? prev : res.data[0].document_name));
        }
      }

      // Pre-fills review decisions
      const decisions = {};
      normalizedDocs.forEach((doc) => {
        decisions[doc.accreditation_document_id] = mapDocStatus(doc.status);
        decisions[doc.document_name] = mapDocStatus(doc.status);
      });
      setDocReviewDecisions(decisions);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDocuments();
  }, [org.id]);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const res = await getAccreditationChecklist(org.id);
        setChecklistItems(res.data.items || []);
      } catch (error) {
        console.error("Failed to fetch checklist:", error);
      }
    };
    fetchChecklist();
  }, [org.id]);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", (data) => {
    if (!data?.org_id || String(data.org_id) === String(org.id)) {
      fetchDocuments(selectedDocument, true);
      getAccreditationChecklist(org.id).then(res => setChecklistItems(res.data.items || [])).catch(() => { });
    }
  });

  useWebSocketEvent("ORGANIZATION_STATUS_UPDATED", (data) => {
    if (!data?.organization_id || String(data.organization_id) === String(org.id)) {
      if (data?.status) {
        setOrgStatus(mapOrgStatusToLabel(data.status));
      }
    }
  });

  // Finds all matching documents under the selected requirement slot
  const matchingDocs = docs.filter(
    (d) => (d.document_name || "").trim().toLowerCase() === (selectedDocument || "").trim().toLowerCase()
  );

  // Resolves the currently active document
  const currentDoc = matchingDocs.find(
    (d) => (d.accreditation_document_id || d.reaccreditation_document_id) === selectedAttachmentId
  ) || matchingDocs[0] || null;

  // Fetches remarks for the selected document whenever it changes
  useEffect(() => {
    const loadRemarks = async () => {
      if (!currentDoc) return;
      try {
        const res = await getAccreditationRemarks(
          processType,
          currentDoc.accreditation_document_id,
        );
        setRemarks(res.data);
      } catch (error) {
        console.error("Failed to load remarks:", error);
      }
    };
    loadRemarks();
  }, [selectedDocument, currentDoc]);

  // Saves the org-level status change to the backend
  const handleOrgStatusChange = async (label) => {
    const statusMap = {
      "For Endorsement to be Accredited": "for_endorsement_accredited",
      "For Endorsement to be Re-accredited": "for_endorsement_reaccredited",
      "For Interview": "for_interview",
    };
    setOrgStatus(label);
    setStatusDropdownOpen(false);

    if (label === "For Interview") {
      setPendingInterviewOrg({ label, status: statusMap[label] });
      setShowInterviewModal(true);
      setInterviewDate('');
      setInterviewMedium('');
      return;
    }

    try {
      if (label && statusMap[label]) {
        await updateOrganizationStatus(org.id, { status: statusMap[label] });
      } else if (!label) {
        await updateOrganizationStatus(org.id, { status: 'pending' });
      }
    } catch (error) {
      console.error("Failed to update organization status:", error);
      alert("Failed to update organization status. Your session may have expired.");
    }
  };

  const handleConfirmInterview = async () => {
    try {
      await updateOrganizationStatus(org.id, {
        status: 'for_interview',
        interview_date: interviewDate || null,
        interview_medium: interviewMedium || null,
      });
      setShowInterviewModal(false);
      setPendingInterviewOrg(null);
    } catch (error) {
      console.error("Failed to update organization status:", error);
      alert("Failed to update status. Please try again.");
    }
  };

  // Saves the document review decision (status + optional note) to the backend.
  const handleConfirmReview = async () => {
    const targetDoc = currentDoc || docs.find((d) => d.document_name === selectedDocument);
    if (!targetDoc) return;
    const targetDocId = targetDoc.accreditation_document_id;
    try {
      setDocReviewDecisions((prev) => ({
        ...prev,
        [targetDocId]: reviewConfirmation.action,
        [targetDoc.document_name]: reviewConfirmation.action,
      }));

      const statusMap = {
        Reviewed: "reviewed",
        "For Revision": "for_revision",
        Rejected: "rejected",
      };
      const backendStatus = statusMap[reviewConfirmation.action];

      await updateAccreditationStatus(processType, targetDocId, {
        status: backendStatus,
      });

      await addAccreditationRemark(
        processType,
        targetDocId,
        reviewNote.trim(),
        reviewConfirmation.action,
      );

      await fetchDocuments(targetDoc.document_name);
      const checklistRes = await getAccreditationChecklist(org.id);
      setChecklistItems(checklistRes.data.items || []);
      setReviewConfirmation({ action: null, document: null, isOpen: false });
      setReviewNote("");
    } catch (error) {
      console.error("Failed to save review:", error);
      const msg = error.response?.data?.detail || error.userMessage || error.message || "Failed to save the review. Please try again.";
      alert(msg);
    }
  };

  // Unique document names from checklist and docs
  const documentNames = Array.from(
    new Set([
      ...checklistItems.map((c) => c.document_name),
      ...docs.map((d) => d.document_name),
    ])
  ).filter(Boolean);

  // Parses the validation issues string into an array for the checklist display
  const parseIssues = (issuesText) => {
    if (!issuesText) return [];
    return issuesText.split(/\d+\.\s/).filter((s) => {
      const cleaned = s.trim().toLowerCase().replace(/[^a-z]/g, '');
      return s.trim().length > 0 && cleaned !== 'none' && cleaned !== 'na' && cleaned !== 'nothing';
    });
  };

  const getStatusBadge = (status) => {
    const norm = (status || "").toLowerCase();
    if (norm.includes("reviewed") || norm.includes("approve")) {
      return {
        label: "Reviewed",
        dotClass: "bg-emerald-500",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    }
    if (norm.includes("revision")) {
      return {
        label: "For Revision",
        dotClass: "bg-amber-500",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
      };
    }
    if (norm.includes("reject")) {
      return {
        label: "Rejected",
        dotClass: "bg-red-500",
        badgeClass: "bg-red-50 text-red-700 border-red-200",
      };
    }
    return {
      label: status || "Note",
      dotClass: "bg-[#086C53]",
      badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
    };
  };

  // Renders the actual uploaded file inline; falls back to extracted text for unsupported types
  const renderFilePreview = (iframeClass = "h-[800px]") => {
    if (!currentDoc) {
      return (
        <div className="h-32 bg-gray-100 rounded border border-gray-200 mt-8 flex items-center justify-center text-gray-400 font-medium">
          No document selected
        </div>
      );
    }
    const fileUrl = `${BASE_URL}/documents/${processType}/${currentDoc.accreditation_document_id}/view`;
    const lowerPath = (currentDoc.file_path || "").toLowerCase();

    // If this is a waiver link submission, show the link instead of file preview
    if (currentDoc.waiver_link) {
      return (
        <div className="mt-4 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm max-w-lg">
          <div className="bg-[#054334] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <div>
                <p className="text-white font-bold text-[15px]">Waiver Link Submitted</p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5">
            <p className="text-[12px] font-bold text-gray-400 uppercase tracking-wider mb-3">Document Link</p>
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <a
                href={currentDoc.waiver_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-[#086C53] text-[13px] font-medium underline truncate hover:text-[#054334] transition-colors"
              >
                {currentDoc.waiver_link}
              </a>
              <button
                onClick={() => navigator.clipboard.writeText(currentDoc.waiver_link)}
                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
                title="Copy link"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2-2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (lowerPath.endsWith(".pdf")) {
      return (
        <iframe
          src={fileUrl}
          className={`w-full ${iframeClass} border border-gray-200 rounded`}
          title={currentDoc.document_name}
        />
      );
    }
    if (lowerPath.endsWith(".docx") || lowerPath.endsWith(".doc")) {
      const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
      return (
        <iframe
          src={previewUrl}
          className={`w-full ${iframeClass} border border-gray-200 rounded`}
          title={currentDoc.document_name}
        />
      );
    }
    if (
      lowerPath.endsWith(".png") ||
      lowerPath.endsWith(".jpg") ||
      lowerPath.endsWith(".jpeg")
    ) {
      return (
        <img
          src={fileUrl}
          alt={currentDoc.document_name}
          className="w-full object-contain"
        />
      );
    }
    return (
      <div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-700 text-sm">
          This document cannot be previewed in the browser.{" "}
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-medium"
          >
            Open / Download
          </a>
        </div>
        {currentDoc.extracted_text && (
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed mt-4">
            {currentDoc.extracted_text}
          </div>
        )}
      </div>
    );
  };

  // Reusable document review dropdown button used in both normal and expanded views.
  // Captures which document is being reviewed at click time (not confirm time) to
  // avoid applying a decision to the wrong document if the selection changes in between.
  const ReviewDropdown = ({ alignLeft }) => {
    const statusObj = getAttachmentStatusLabel({
      document_name: selectedDocument,
      status: currentDoc?.status,
    });
    const currentAction =
      statusObj.text !== "Uploaded" && statusObj.text !== "Not Submitted"
        ? statusObj.text
        : null;
    return (
      <div className="relative">
        <button
          onClick={() => {
            setDocReviewDropdownOpen(!docReviewDropdownOpen);
            setStatusDropdownOpen(false);
          }}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 px-4 rounded-lg focus:outline-none transition-colors text-sm font-medium flex items-center space-x-2"
        >
          {currentAction && (
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${currentAction === "Reviewed"
                ? "bg-green-500"
                : currentAction === "For Revision"
                  ? "bg-orange-500"
                  : "bg-red-500"
                }`}
            ></span>
          )}
          <span>{currentAction || "Review"}</span>
          <Icons.ChevronDown />
        </button>
        {docReviewDropdownOpen && (
          <div
            className={`absolute top-full ${alignLeft ? "left-0" : "right-0"} mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden text-left`}
          >
            {[
              { label: "Reviewed", color: "bg-green-500" },
              { label: "For Revision", color: "bg-orange-500" },
              { label: "Rejected", color: "bg-red-500" },
            ].map((actionItem) => (
              <div
                key={actionItem.label}
                className={`px-4 py-2.5 cursor-pointer text-sm transition-colors flex items-center space-x-3 ${currentAction === actionItem.label
                  ? "bg-gray-50"
                  : "hover:bg-gray-50"
                  }`}
                onClick={() => {
                  setReviewConfirmation({
                    action: actionItem.label,
                    document: selectedDocument,
                    isOpen: true,
                  });
                  setDocReviewDropdownOpen(false);
                }}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${actionItem.color}`}
                ></span>
                <div className="flex-1 min-w-0">
                  <span
                    className={`font-medium ${currentAction === actionItem.label
                      ? "text-gray-900"
                      : "text-gray-700"
                      }`}
                  >
                    {actionItem.label}
                  </span>
                </div>
                {currentAction === actionItem.label && (
                  <svg
                    className="w-4 h-4 text-gray-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 relative h-[calc(100vh-160px)] flex flex-col overflow-hidden min-w-0">
      {/* Top Header */}
      <div className="flex justify-between items-start space-x-4 shrink-0">
        <div className="flex-1 min-w-0">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 flex items-center space-x-2 font-medium mb-3"
          >
            <Icons.ChevronLeft />
            <span>Back</span>
          </button>
          <h2 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight mb-2 break-words">
            {org.orgFullName || org.org}
          </h2>
          <p className="text-gray-500 text-sm">President: {org.repName}</p>
          <p className="text-gray-500 text-sm">
            Documents submitted: {docs.length}
          </p>
        </div>

        {/* Status Dropdown — connected to the backend, color-coded by current status */}
        <div className="relative">
          <button
            onClick={() => {
              setStatusDropdownOpen(!statusDropdownOpen);
              setDocReviewDropdownOpen(false);
            }}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 px-4 rounded-lg focus:outline-none transition-colors text-sm font-medium flex items-center space-x-2"
          >
            {orgStatus && (
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${orgStatus === "For Endorsement to be Accredited"
                  ? "bg-green-500"
                  : orgStatus === "For Endorsement to be Re-accredited"
                    ? "bg-emerald-800"
                    : "bg-blue-500"
                  }`}
              ></span>
            )}
            <span>{orgStatus || "Status"}</span>
            <Icons.ChevronDown />
          </button>
          {statusDropdownOpen && (
            <div className="absolute top-full right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 text-left overflow-hidden">
              {[
                { label: "For Endorsement to be Accredited", color: "bg-green-500" },
                { label: "For Endorsement to be Re-accredited", color: "bg-emerald-800" },
                { label: "For Interview", color: "bg-blue-500" },
              ].map((status) => (
                <div
                  key={status.label}
                  className={`px-4 py-2.5 cursor-pointer text-sm transition-colors flex items-center space-x-3 ${orgStatus === status.label ? "bg-gray-50" : "hover:bg-gray-50"}`}
                  onClick={() => handleOrgStatusChange(status.label)}
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${status.color} flex-shrink-0`}
                  ></span>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`font-medium ${orgStatus === status.label ? "text-gray-900" : "text-gray-700"}`}
                    >
                      {status.label}
                    </div>
                  </div>
                  {orgStatus === status.label && (
                    <svg
                      className="w-4 h-4 text-[#086C53] flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              ))}
              <div
                className="px-4 py-2.5 cursor-pointer text-sm transition-colors flex items-center space-x-3 hover:bg-gray-50 border-t border-gray-100 text-gray-500"
                onClick={() => {
                  setOrgStatus(null);
                  setStatusDropdownOpen(false);
                  updateOrganizationStatus(org.id, { status: "pending" }).catch(
                    (err) =>
                      console.error("Failed to reset org status:", err),
                  );
                }}
              >
                <div className="w-2.5 h-2.5 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">None</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[240px_1fr_280px] gap-6 flex-1 min-h-0 min-w-0 pb-2">
        {/* Left Panel: Attachments */}
        <div className="bg-gray-100 rounded-xl p-5 flex flex-col h-full min-h-0">
          <h3 className="font-bold text-gray-800 mb-4 text-base shrink-0">
            Attachments
          </h3>
          <div className="overflow-y-auto flex-1 space-y-4 pr-2 custom-scrollbar min-h-0">
            {checklistItems.length === 0 ? (
              <p className="text-xs text-gray-400">
                No documents submitted yet.
              </p>
            ) : (
              [...checklistItems]
                .sort((a, b) => {
                  const aUp = a.status !== "not_submitted";
                  const bUp = b.status !== "not_submitted";
                  return aUp === bUp ? 0 : aUp ? -1 : 1;
                })
                .map((item) => {
                  const uploaded = item.status !== "not_submitted";
                  const statusObj = getAttachmentStatusLabel(item);
                  const itemMatchingDocs = docs.filter(
                    (d) => (d.document_name || "").trim().toLowerCase() === (item.document_name || "").trim().toLowerCase()
                  );
                  const isSelected = (selectedDocument || "").trim().toLowerCase() === (item.document_name || "").trim().toLowerCase();

                  return (
                    <div
                      key={item.document_name}
                      className={`flex items-start space-x-3 p-2 rounded-lg transition-colors ${uploaded ? "cursor-pointer hover:bg-gray-200/60" : "cursor-default opacity-50"
                        } ${isSelected ? "bg-gray-200" : ""}`}
                      onClick={() => {
                        if (uploaded) {
                          setSelectedDocument(item.document_name);
                          if (itemMatchingDocs.length > 0) {
                            setSelectedAttachmentId(itemMatchingDocs[0].accreditation_document_id);
                          }
                        }
                      }}
                    >
                      <div className="mt-0.5">
                        {uploaded ? (
                          <Icons.CheckCircle />
                        ) : (
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <circle cx="12" cy="12" r="9" strokeWidth="1.5" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <div
                            className={`text-sm font-semibold truncate ${uploaded ? "text-gray-800" : "text-gray-400"}`}
                          >
                            {item.document_name}
                          </div>
                          {itemMatchingDocs.length > 1 && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 bg-[#e0f5ee] text-[#086C53] rounded-full shrink-0">
                              {itemMatchingDocs.length} files
                            </span>
                          )}
                        </div>
                        <div
                          className={`text-[10px] font-medium uppercase tracking-wider mt-0.5 ${!uploaded ? "text-gray-400" : statusObj.color
                            }`}
                        >
                          {!uploaded ? "Not Submitted" : statusObj.text}
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            {(() => {
              const submittedCount = checklistItems.filter(
                (i) => i.status !== "not_submitted",
              ).length;
              const total = checklistItems.length || 15;
              return (
                <>
                  <div className="text-xs text-gray-500 mb-2 font-medium">
                    {submittedCount} of {total} documents uploaded
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
                    <div
                      className="h-full bg-[#086C53]"
                      style={{ width: `${(submittedCount / total) * 100}%` }}
                    ></div>
                  </div>
                </>
              );
            })()}
            <button
              onClick={() => setHistoryModalOpen(true)}
              className="w-full bg-[#086C53] hover:bg-[#065440] text-white py-2.5 rounded-lg flex items-center justify-center space-x-2 text-sm font-medium transition-colors shadow-sm"
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Review History</span>
            </button>
          </div>
        </div>

        {/* Middle Panel: Document Viewer */}
        <div className="bg-gray-100 rounded-xl p-5 flex flex-col h-full border border-gray-200 shadow-sm relative min-h-0 min-w-0 overflow-hidden">
          <div className="flex justify-between items-center mb-4 shrink-0">
            <div>
              <h3 className="font-bold text-gray-800 text-sm mb-1">
                Select document
              </h3>
              <div className="relative">
                <select
                  value={selectedDocument}
                  onChange={(e) => {
                    setSelectedDocument(e.target.value);
                    const matched = docs.filter(
                      (d) => (d.document_name || "").trim().toLowerCase() === e.target.value.trim().toLowerCase()
                    );
                    if (matched.length > 0) {
                      setSelectedAttachmentId(matched[0].accreditation_document_id);
                    }
                  }}
                  className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-4 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium w-64"
                >
                  {documentNames.map((doc) => (
                    <option key={doc} value={doc}>
                      {doc}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                  <Icons.ChevronDown />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-5">
              <ReviewDropdown alignLeft={false} />
              <button
                onClick={() => setIsExpandedView(true)}
                className="bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800 py-2 px-2.5 rounded-lg transition-colors cursor-pointer"
                title="Expand view"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Attachment Tabs when multiple files are attached under this requirement */}
          {matchingDocs.length > 1 && (
            <div className="flex items-center space-x-2 mb-3 overflow-x-auto pb-1 shrink-0">
              <span className="text-xs font-bold text-gray-500 mr-1 shrink-0">Files ({matchingDocs.length}):</span>
              {matchingDocs.map((doc, idx) => {
                const isSelected = (currentDoc?.accreditation_document_id) === (doc.accreditation_document_id);
                const st = getAttachmentStatusLabel(doc);
                return (
                  <button
                    key={doc.accreditation_document_id || idx}
                    onClick={() => setSelectedAttachmentId(doc.accreditation_document_id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 shrink-0 border cursor-pointer ${isSelected
                      ? "bg-[#086C53] text-white border-[#086C53] shadow-sm"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    <span>Attachment {idx + 1}</span>
                    <span
                      className={`w-2 h-2 rounded-full ${st.text === "Reviewed"
                        ? "bg-green-400"
                        : st.text === "For Revision"
                          ? "bg-orange-400"
                          : st.text === "Rejected"
                            ? "bg-red-400"
                            : "bg-gray-300"
                        }`}
                    />
                  </button>
                );
              })}
            </div>
          )}

          {/* Scrollable Document Container — shows the real uploaded file */}
          <div className="flex-1 overflow-y-auto bg-[#e5e7eb] rounded-lg relative custom-scrollbar border border-gray-300 min-h-0 p-4">
            <div className="bg-white max-w-[816px] w-full min-h-[1056px] p-4 shadow-md m-auto relative rounded-sm">
              <div className="space-y-4">{renderFilePreview("h-[850px]")}</div>
            </div>
          </div>
        </div>

        {/* Expanded Document Viewer Modal */}
        {isExpandedView &&
          ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
              <div className="bg-gray-100 rounded-2xl w-full h-full max-w-6xl max-h-full flex flex-col shadow-2xl border border-gray-200 overflow-hidden">
                {/* Expanded Header */}
                <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200 shrink-0">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm mb-1">
                        Select document
                      </h3>
                      <div className="relative">
                        <select
                          value={selectedDocument}
                          onChange={(e) => {
                            setSelectedDocument(e.target.value);
                            const matched = docs.filter(
                              (d) => (d.document_name || "").trim().toLowerCase() === e.target.value.trim().toLowerCase()
                            );
                            if (matched.length > 0) {
                              setSelectedAttachmentId(matched[0].accreditation_document_id);
                            }
                          }}
                          className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-4 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium w-64"
                        >
                          {documentNames.map((doc) => (
                            <option key={doc} value={doc}>
                              {doc}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                          <Icons.ChevronDown />
                        </div>
                      </div>
                    </div>
                    <div className="mt-5">
                      <ReviewDropdown alignLeft={true} />
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsExpandedView(false);
                      setDocReviewDropdownOpen(false);
                    }}
                    className="bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800 py-2 px-2.5 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
                    title="Minimize view"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                      />
                    </svg>
                    <span className="text-sm font-medium">Minimize</span>
                  </button>
                </div>

                {/* Attachment tabs inside expanded view */}
                {matchingDocs.length > 1 && (
                  <div className="flex items-center space-x-2 px-8 py-2.5 bg-gray-50 border-b border-gray-200 shrink-0 overflow-x-auto">
                    <span className="text-xs font-bold text-gray-500 mr-1 shrink-0">Files ({matchingDocs.length}):</span>
                    {matchingDocs.map((doc, idx) => {
                      const isSelected = (currentDoc?.accreditation_document_id) === (doc.accreditation_document_id);
                      const st = getAttachmentStatusLabel(doc);
                      return (
                        <button
                          key={doc.accreditation_document_id || idx}
                          onClick={() => setSelectedAttachmentId(doc.accreditation_document_id)}
                          className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center space-x-2 shrink-0 border cursor-pointer ${isSelected
                            ? "bg-[#086C53] text-white border-[#086C53] shadow-sm"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                            }`}
                        >
                          <span>Attachment {idx + 1}</span>
                          <span
                            className={`w-2 h-2 rounded-full ${st.text === "Reviewed"
                              ? "bg-green-400"
                              : st.text === "For Revision"
                                ? "bg-orange-400"
                                : st.text === "Rejected"
                                  ? "bg-red-400"
                                  : "bg-gray-300"
                              }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Expanded Document Content */}
                <div className="flex-1 overflow-y-auto bg-[#e5e7eb] p-6 custom-scrollbar min-h-0">
                  <div className="bg-white max-w-[900px] w-full min-h-[1056px] p-6 shadow-lg m-auto relative rounded">
                    <div className="space-y-5">
                      {renderFilePreview("h-[1000px]")}
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Right Panel: AI Validation Result */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full min-h-0">
          <div className="bg-[#f0f4f8] border-b border-gray-200 p-4 flex items-center space-x-2 shrink-0">
            <h3 className="font-bold text-[#1a365d] text-sm">AI Summary</h3>
          </div>

          <div className="p-5 flex-1 overflow-y-auto custom-scrollbar">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Overall AI Assessment
            </div>
            {currentDoc ? (
              (currentDoc.waiver_link || (currentDoc.document_name || '').toLowerCase().includes('waiver')) ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-500">
                  Waivers are submitted as links and not validated by AI. Only coordinator review applies.
                </div>
              ) : (
                <>
                  <div
                    className={`rounded-full px-3 py-1.5 inline-flex items-center space-x-2 mb-3 text-xs font-bold border ${currentDoc.validation_recommendation === "Approved"
                      ? "bg-green-100 text-green-700 border-green-200"
                      : currentDoc.validation_recommendation === "Rejected"
                        ? "bg-red-100 text-red-700 border-red-200"
                        : "bg-orange-100 text-orange-700 border-orange-200"
                      }`}
                  >
                    <div className="w-1.5 h-1.5 bg-current rounded-full"></div>
                    <span>
                      {currentDoc.validation_recommendation || "Pending Review"}
                      {currentDoc.validation_score != null
                        ? ` — Score: ${currentDoc.validation_score}`
                        : ""}
                    </span>
                  </div>

                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Issues Detected
                  </div>
                  <div className="space-y-3">
                    {parseIssues(currentDoc.validation_issues).length === 0 ? (
                      <div className="flex items-start space-x-2">
                        <div className="mt-0.5 text-green-500 bg-green-100 rounded-full p-0.5">
                          <Icons.Check />
                        </div>
                        <span className="text-xs text-gray-700 font-medium">
                          No issues detected
                        </span>
                      </div>
                    ) : (
                      parseIssues(currentDoc.validation_issues).map(
                        (issue, i) => (
                          <div key={i} className="flex items-start space-x-2">
                            <div className="mt-0.5 bg-orange-100 rounded-full p-0.5">
                              <Icons.Exclamation />
                            </div>
                            <span className="text-xs text-gray-700 font-medium">
                              {issue.trim()}
                            </span>
                          </div>
                        ),
                      )
                    )}
                  </div>
                </>
              )
            ) : (
              <p className="text-xs text-gray-400">
                Select a document to view its AI validation.
              </p>
            )}

            {/* Shows admin remarks for this document */}
            {remarks.length > 0 && (
              <>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 mt-6">
                  Admin Remarks
                </div>
                <div className="space-y-2.5">
                  {remarks.map((r, i) => {
                    const badge = getStatusBadge(r.status);
                    return (
                      <div
                        key={i}
                        className="text-xs text-gray-700 bg-white p-2.5 rounded-lg border border-gray-200 shadow-xs space-y-1.5"
                      >
                        <div className="flex items-center justify-between">
                          {r.status ? (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.badgeClass}`}>
                              {badge.label}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                              Note
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400 font-medium">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                              : ""}
                          </span>
                        </div>
                        {r.content && (
                          <div className="text-xs text-gray-700 leading-relaxed break-words bg-gray-50/70 p-2 rounded border border-gray-100">
                            {r.content}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Review Confirmation Modal — rendered via portal; Confirm calls the real
          backend update through handleConfirmReview */}
      {reviewConfirmation.isOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {reviewConfirmation.action}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {reviewConfirmation.document || selectedDocument}
                </p>

                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#086C53] focus:border-transparent resize-none"
                    rows="4"
                    placeholder="Add notes about this decision..."
                  ></textarea>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setReviewConfirmation({
                        action: null,
                        document: null,
                        isOpen: false,
                      });
                      setReviewNote("");
                    }}
                    className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmReview}
                    className="px-5 py-2 bg-[#086C53] text-white rounded-lg text-sm font-bold hover:bg-[#065440] transition-colors shadow-sm"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Review History Modal — shows real remarks, rendered via portal */}
      {historyModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">
                  Review History
                </h3>
                <button
                  onClick={() => setHistoryModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icons.Close />
                </button>
              </div>
              <div className="p-6 max-h-96 overflow-y-auto custom-scrollbar space-y-5">
                {remarks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No review history yet for this document.
                  </p>
                ) : (
                  remarks.map((r, i) => {
                    const badge = getStatusBadge(r.status);
                    return (
                      <div
                        key={i}
                        className="border-l-2 border-gray-200 pl-4 relative"
                      >
                        <div
                          className={`absolute w-2.5 h-2.5 ${badge.dotClass} rounded-full -left-[6px] top-1.5 border-2 border-white shadow-sm`}
                        ></div>
                        <div className="flex items-center space-x-2">
                          {r.status && (
                            <span
                              className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${badge.badgeClass}`}
                            >
                              {badge.label}
                            </span>
                          )}
                          <span className="text-[11px] text-gray-400 font-medium">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleDateString("en-US", {
                                month: "numeric",
                                day: "numeric",
                                year: "numeric",
                              })
                              : ""}
                          </span>
                        </div>
                        {r.content && (
                          <div className="text-xs text-gray-700 mt-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100 leading-relaxed break-words">
                            {r.content}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showInterviewModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowInterviewModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}>
            <h3 className="text-[18px] font-bold text-gray-900 mb-1">Set Interview Details</h3>
            <p className="text-[13px] text-gray-500 mb-5">
              Provide the interview schedule for this organization. Both fields are optional.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-bold text-gray-700 mb-1.5">
                  Interview Date
                </label>
                <input
                  type="date"
                  value={interviewDate}
                  onChange={(e) => setInterviewDate(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-gray-700 mb-1.5">
                  Interview Medium
                </label>
                <input
                  type="text"
                  value={interviewMedium}
                  onChange={(e) => setInterviewMedium(e.target.value)}
                  placeholder="e.g. Online via Google Meet, Face to face at OSAS Office"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowInterviewModal(false)}
                className="flex-1 py-2.5 rounded-xl text-[14px] font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmInterview}
                className="flex-1 py-2.5 rounded-xl text-[14px] font-bold text-white transition-colors"
                style={{ background: '#054334' }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Accreditation Page ---
const Accreditation = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("Filter");
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [batchActionsOpen, setBatchActionsOpen] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0, flip: false });
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewingApp, setViewingApp] = useState(false);

  // Real data from the backend
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 6;

  // Maps backend document statuses to a frontend overall status label.
  // Uses the full label forms ("For Interview"/"For Revision") consistently
  // with the detail page's document review labels.
  const computeStatus = (items) => {
    if (!items || items.length === 0) return "Pending";
    const submitted = items.filter(
      (i) => i.status && i.status !== "not_submitted",
    );
    if (submitted.length === 0) return "Pending";
    const statuses = submitted.map((i) => i.status);
    if (statuses.every((s) => s === "reviewed")) return "Reviewed";
    if (statuses.some((s) => s === "for_revision")) return "For Revision/s";
    if (statuses.some((s) => s === "rejected")) return "Rejected";
    return "Pending";
  };

  // Fetches all organizations and their accreditation checklist progress
  const fetchData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const orgsRes = await getAllOrganizations();
      const usersRes = await getAllUsers();
      const users = usersRes.data;

      // Show all organizations (they can be filtered further in the UI)
      const activeOrgs = orgsRes.data;

      // For each organization, fetch its checklist to get completion data
      const rows = await Promise.all(
        activeOrgs.map(async (org) => {
          const rep = users.find(
            (u) =>
              u.organization_id === org.organization_id &&
              u.role === "end_user",
          );
          let completion = 0;
          let missing = 15;
          // eslint-disable-next-line no-unused-vars
          let status = "Pending";
          let hasSubmitted = false;
          try {
            const checklistRes = await getAccreditationChecklist(
              org.organization_id,
            );
            const data = checklistRes.data;
            completion = data.progress_percentage || 0;
            missing = data.not_submitted != null ? data.not_submitted : 15;
            status = computeStatus(data.items);
            if (data.items && data.items.some(i => i.status && i.status !== "not_submitted")) {
              hasSubmitted = true;
            }
          } catch (e) {
            // If checklist not found, org has not submitted anything yet
          }
          let orgStatusLabel = "Pending";
          if (org.status === "for_endorsement_accredited") orgStatusLabel = "Accredited";
          else if (org.status === "for_endorsement_reaccredited") orgStatusLabel = "Re-accredited";
          else if (org.status === "for_interview") orgStatusLabel = "For Interview";

          return {
            id: org.organization_id,
            org: org.acronym || org.name
              .split(' ')
              .filter(w => w.length > 0)
              .map(w => w[0])
              .join('')
              .toUpperCase(),
            orgFullName: org.name,
            repName: rep ? rep.full_name : "No representative",
            email: rep ? rep.slsu_email : org.contact_email || "N/A",
            college: org.college || "N/A",
            completion: Math.round(completion * 100) / 100,
            missing: missing,
            status: orgStatusLabel,
            rawStatus: org.status,
            hasSubmitted: hasSubmitted,
            orgType: org.org_type || "new",  // 'new' = Accreditation, 'existing' = Re-accreditation
          };
        }),
      );

      // Only show organizations that have submitted at least one document
      setTableData(rows.filter(row => row.hasSubmitted));
    } catch (error) {
      console.error("Failed to fetch accreditation data:", error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, []);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", () => {
    fetchData(true);
  });

  // If navigated from the Dashboard with a specific org to open, auto-open its detail
  useEffect(() => {
    if (!loading && tableData.length > 0 && location.state?.openOrgId) {
      const target = tableData.find(r => r.id === location.state.openOrgId);
      if (target) setViewingApp(target);
    }
  }, [loading, tableData, location.state]);

  // Filters the data by search term and status filter
  const filteredData = tableData.filter((row) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      row.org.toLowerCase().includes(term) ||
      row.orgFullName.toLowerCase().includes(term) ||
      row.repName.toLowerCase().includes(term) ||
      (row.college && row.college.toLowerCase().includes(term));

    let matchesFilter = true;
    if (selectedFilter === "For Endorsement to be Accredited")
      matchesFilter = row.status === "Accredited";
    else if (selectedFilter === "For Endorsement to be Re-accredited")
      matchesFilter = row.status === "Re-accredited";
    else if (selectedFilter === "For Interview")
      matchesFilter = row.status === "For Interview";

    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePrev = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const handlePageClick = (page) => setCurrentPage(page);

  const toggleSelectRow = (id, e) => {
    e.stopPropagation();
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Accredited":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-[11px] font-bold">
            Accredited
          </span>
        );
      case "Re-accredited":
        return (
          <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-[11px] font-bold">
            Re-accredited
          </span>
        );
      case "For Interview":
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-[11px] font-bold">
            For Interview
          </span>
        );
      case "Pending":
      default:
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-[11px] font-bold">
            Pending
          </span>
        );
    }
  };

  if (viewingApp) {
    return (
      <AccreditationDetails
        org={viewingApp}
        onBack={() => {
          setViewingApp(false);
          fetchData();
        }}
      />
    );
  }

  if (loading) {
    return <AdminAccreditationSkeleton rows={6} />;
  }

  return (
    <div className="space-y-6 relative pb-16">
      <div>
        <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">
          Accreditation Applications
        </h2>
        <p className="text-gray-500 text-sm mt-1">
          Review and process organization accreditation requirements.
        </p>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3 w-full md:w-auto flex-wrap gap-y-2">
          <div className="relative w-full md:w-[400px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icons.Search />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by organization name..."
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium"
            />
          </div>

          <div className="relative">
            <button
              onClick={() => {
                setFilterDropdownOpen(!filterDropdownOpen);
                setBatchActionsOpen(false);
              }}
              className="bg-white border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg focus:outline-none hover:bg-gray-50 transition-colors text-sm font-medium flex items-center space-x-2"
            >
              <Icons.Filter />
              <span>{selectedFilter}</span>
            </button>
            {filterDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                {[
                  "All",
                  "For Endorsement to be Accredited",
                  "For Endorsement to be Re-accredited",
                  "For Interview",
                ].map((opt) => (
                  <div
                    key={opt}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700"
                    onClick={() => {
                      setSelectedFilter(opt === "All" ? "Filter" : opt);
                      setFilterDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {opt}
                  </div>
                ))}
              </div>
            )}
          </div>

          {!isSelectMode ? (
            <button
              onClick={() => setIsSelectMode(true)}
              className="bg-white border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg focus:outline-none hover:bg-gray-50 transition-colors text-sm font-medium flex items-center space-x-2"
            >
              <Icons.Select />
              <span>Select</span>
            </button>
          ) : (
            <div className="relative flex items-center space-x-2">
              <button
                onClick={() => setBatchActionsOpen(!batchActionsOpen)}
                className="bg-[#086C53] text-white py-2.5 px-4 rounded-lg focus:outline-none hover:bg-[#065440] transition-colors text-sm font-medium flex items-center space-x-2 shadow-sm"
              >
                <span>Batch Actions</span>
                <Icons.ChevronDown />
              </button>
              <button
                onClick={() => {
                  setIsSelectMode(false);
                  setSelectedRows([]);
                  setBatchActionsOpen(false);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline px-2 font-medium"
              >
                Cancel
              </button>

              {batchActionsOpen && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-[#086C53] border border-[#065440] rounded-lg shadow-lg z-50 py-1 text-white overflow-hidden">
                  <div
                    className="px-4 py-2.5 hover:bg-white/10 cursor-pointer text-sm flex items-center space-x-3 transition-colors"
                    onClick={() => setBatchActionsOpen(false)}
                  >
                    <Icons.Check />
                    <span>For Endorsement to be Accredited</span>
                  </div>
                  <div
                    className="px-4 py-2.5 hover:bg-white/10 cursor-pointer text-sm flex items-center space-x-3 transition-colors"
                    onClick={() => setBatchActionsOpen(false)}
                  >
                    <Icons.Check />
                    <span>For Endorsement to be Re-accredited</span>
                  </div>
                  <div
                    className="px-4 py-2.5 hover:bg-white/10 cursor-pointer text-sm flex items-center space-x-3 transition-colors"
                    onClick={() => setBatchActionsOpen(false)}
                  >
                    <Icons.User />
                    <span>For Interview</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      <div
        className="bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-visible"
        onClick={() => setActionDropdownOpen(null)}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed min-w-[760px]">
            <thead>
              <tr className="bg-[#086C53] text-white">
                {isSelectMode && (
                  <th className="px-4 py-4 w-12 text-center rounded-tl-[12px]"></th>
                )}
                <th
                  className={`px-6 py-4 text-xs font-bold uppercase tracking-wider w-[24%] min-w-[170px] ${!isSelectMode ? "rounded-tl-[12px]" : ""}`}
                >
                  Organization
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[22%] min-w-[150px]">
                  Representative
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[15%] min-w-[120px]">
                  College/Campus
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[17%] min-w-[130px]">
                  Completion
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[13%] min-w-[110px]">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right rounded-tr-[12px] w-[9%] min-w-[80px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan={isSelectMode ? 7 : 6}
                    className="px-6 py-10 text-center text-gray-400 text-sm"
                  >
                    No accreditation applications found.
                  </td>
                </tr>
              ) : (
                currentData.map((row) => (
                  <tr
                    key={row.id}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedRows.includes(row.id) ? "bg-gray-50" : ""}`}
                    onClick={() => setViewingApp(row)}
                  >
                    {isSelectMode && (
                      <td
                        className="px-4 py-4"
                        onClick={(e) => toggleSelectRow(row.id, e)}
                      >
                        <div
                          className={`w-5 h-5 rounded border ${selectedRows.includes(row.id) ? "bg-[#086C53] border-[#086C53] text-white flex items-center justify-center" : "border-gray-300 bg-white"}`}
                        >
                          {selectedRows.includes(row.id) && (
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div
                        className="text-sm font-medium text-gray-700 break-words line-clamp-2 leading-snug"
                        title={row.orgFullName ? `${row.org} - ${row.orgFullName}` : row.org}
                      >
                        {row.org}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold mt-1 ${row.orgType === 'existing'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-green-100 text-green-700'
                        }`}>
                        {row.orgType === 'existing' ? 'Re-accreditation' : 'Accreditation'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm font-medium text-gray-700 truncate"
                        title={row.repName}
                      >
                        {row.repName}
                      </div>
                      <div
                        className="text-[11px] text-gray-400 truncate"
                        title={row.email}
                      >
                        {row.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                      {row.college}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 mb-1.5">
                        <span className="text-[10px] font-medium text-gray-600">
                          {row.completion}%
                        </span>
                        {row.missing > 0 && (
                          <span className="text-[9px] bg-red-100 text-red-500 font-bold px-1.5 py-[1px] rounded-sm">
                            {row.missing} missing
                          </span>
                        )}
                      </div>
                      <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#086C53]"
                          style={{ width: `${row.completion}%` }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(row.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const dropdownHeight = 256;
                          const spaceBelow = window.innerHeight - rect.bottom;
                          const flip = spaceBelow < dropdownHeight + 180;
                          setDropdownPos({
                            x: rect.right,
                            y: rect.bottom,
                            top: rect.top,
                            flip,
                          });
                          setActionDropdownOpen(
                            actionDropdownOpen === row.id ? null : row.id,
                          );
                        }}
                        className="w-8 h-8 rounded-full hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-500 ml-auto"
                      >
                        <Icons.DotsVertical />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Fixed-position action dropdown — renders outside all overflow contexts */}
      {actionDropdownOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setActionDropdownOpen(null)}
          />
          <div
            className="fixed z-50 w-44 bg-white border border-gray-200 rounded-lg shadow-2xl py-1 text-left text-gray-600 font-medium overflow-hidden"
            style={
              dropdownPos.flip
                ? { top: dropdownPos.y - 148, left: dropdownPos.x - 176 }
                : { top: dropdownPos.y + 4, left: dropdownPos.x - 176 }
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-xs flex items-center space-x-2 transition-colors border-b border-gray-100"
              onClick={() => {
                setViewingApp(
                  currentData.find((r) => r.id === actionDropdownOpen),
                );
                setActionDropdownOpen(null);
              }}
            >
              <Icons.Eye />
              <span>View / Review</span>
            </div>
            <div
              className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-xs flex items-center space-x-2 transition-colors"
              onClick={() => setActionDropdownOpen(null)}
            >
              <Icons.Close />
              <span>Close</span>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-center mt-8">
        <div className="flex items-center space-x-2 bg-white rounded-xl px-3 py-1.5 shadow-sm border border-gray-100">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-2 transition-all ${currentPage === 1 ? "border-gray-300 text-gray-300" : "border-gray-500 text-gray-500 hover:border-gray-600 hover:text-gray-700"}`}
          >
            <Icons.ChevronLeft />
          </button>
          {[...Array(totalPages)].map((_, idx) => {
            const page = idx + 1;
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] transition-all ${isActive
                  ? "bg-[#125845] text-white border-[3px] border-[#91c3b1] shadow-sm"
                  : "bg-white text-gray-600 border-2 border-gray-500 hover:border-gray-600 hover:text-gray-800"
                  }`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-2 transition-all ${currentPage === totalPages ? "border-gray-300 text-gray-300" : "border-gray-500 text-gray-500 hover:border-gray-600 hover:text-gray-700"}`}
          >
            <Icons.ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Accreditation;
